import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, adminAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [ipAddress, setIpAddress] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);

  useEffect(() => {
    // Fetch full user data including device info
    const fetchUserData = async () => {
      try {
        const response = await authAPI.getMe();
        setDeviceInfo(response.device);
        setIpAddress(response.current_ip);
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    // Fetch order statistics
    const fetchOrderStats = async () => {
      try {
        const response = await adminAPI.getOrderStats();
        setOrderStats(response.stats);
      } catch (err) {
        console.error('Error fetching order stats:', err);
      }
    };

    // Fetch daily order statistics
    const fetchDailyStats = async () => {
      try {
        const response = await adminAPI.getDailyOrderStats();
        setDailyStats(response.data);
      } catch (err) {
        console.error('Error fetching daily stats:', err);
      }
    };

    fetchUserData();
    fetchOrderStats();
    fetchDailyStats();
  }, []);
  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="dashboard-card">
            <a
              href="/help/"
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-help-btn"
              title="Open user guide"
            >
              ?
            </a>
            <h2>Dashboard</h2>
            <p>You are successfully logged in with a verified device.</p>
            
            <div className="dashboard-modules">
              <h3>Quick Access</h3>
              <div className="module-grid">
                <button
                  className="module-card"
                  onClick={() => navigate('/customers')}
                >
                  <div className="module-icon icon-gradient">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="url(#purpleGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <h4>Customers</h4>
                  <p>Manage customer information</p>
                  <div className="submenu">
                    <span onClick={(e) => { e.stopPropagation(); navigate('/customers'); }}>List Customers</span>
                    <span onClick={(e) => { e.stopPropagation(); navigate('/customers/new'); }}>New Customer</span>
                    <span onClick={(e) => { e.stopPropagation(); navigate('/customer-fields'); }}>Custom Fields</span>
                  </div>
                </button>
                
                <button
                  className="module-card"
                  onClick={() => navigate('/orders')}
                >
                  <div className="module-icon icon-gradient">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="url(#purpleGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                  </div>
                  <h4>Orders</h4>
                  <p>Manage customer orders</p>
                  <div className="submenu">
                    <span onClick={(e) => { e.stopPropagation(); navigate('/orders'); }}>List Orders</span>
                    <span onClick={(e) => { e.stopPropagation(); navigate('/orders/new'); }}>New Order</span>
                  </div>
                </button>
                
                <button
                  className="module-card"
                  onClick={() => navigate('/deliveries')}
                >
                  <div className="module-icon icon-gradient">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="url(#purpleGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  </div>
                  <h4>Deliveries</h4>
                  <p>Manage deliveries and runs</p>
                  <div className="submenu">
                    <span onClick={(e) => { e.stopPropagation(); navigate('/deliveries/runs'); }}>Delivery Diary</span>
                    <span onClick={(e) => { e.stopPropagation(); navigate('/deliveries/planning'); }}>Planning</span>
                    <span onClick={(e) => { e.stopPropagation(); navigate('/deliveries/vehicles'); }}>Vehicle Availability</span>
                  </div>
                </button>
              </div>
              <svg width="0" height="0">
                <defs>
                  <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {orderStats && (
              <div className="order-stats">
                <h3>Order Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.unscheduled_orders}</span>
                    <span className="stat-label">Unscheduled</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.scheduled_future_orders}</span>
                    <span className="stat-label">Scheduled</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.orders_today}</span>
                    <span className="stat-label">Orders Today</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.orders_this_week}</span>
                    <span className="stat-label">This Week</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.orders_this_month}</span>
                    <span className="stat-label">{orderStats.orders_this_month_name}</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-value">{orderStats.orders_last_month}</span>
                    <span className="stat-label">Last Month ({orderStats.orders_last_month_name})</span>
                  </div>
                </div>
              </div>
            )}

            {dailyStats && dailyStats.length > 0 && (
              <div className="daily-orders-chart">
                <h3>Orders vs Deliveries (Past 14 Days)</h3>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="received" name="Received" fill="#667eea" />
                      <Bar dataKey="delivered" name="Delivered" fill="#764ba2" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="user-details">
              <h3>Your Account</h3>
              <dl>
                <dt>Username:</dt>
                <dd>{user.username}</dd>
                <dt>Email:</dt>
                <dd>{user.email}</dd>
                <dt>User ID:</dt>
                <dd>{user.id}</dd>
              </dl>
            </div>

            <div className="info-box">
              <h3>About Device Authentication</h3>
              <p>
                Your device has been verified and you can now access all features.
                If you log in from a new device, you'll need to verify it via email.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
